
export interface ChapterMetadata {
  title: string;
  slug: string;
  readingTime: number;
}

export interface TutorialMetadata {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  author: string;
  lastUpdated: string;
  featuredImage: string;
  tags: string[];
  chapters: ChapterMetadata[];
}

// This is your actual tutorial data
export const tutorials: TutorialMetadata[] = [
  {
    id: 'server-admin',
    title: 'Modern Server Administration',
    description: 'Learn the fundamentals of modern server administration, from bare metal to containerization and beyond.',
    difficulty: 'intermediate',
    author: 'Mate4Tech',
    lastUpdated: '2025-04-01',
    featuredImage: 'https://images.unsplash.com/photo-1498050108023-c5249f4df085',
    tags: ['Server', 'DevOps', 'Linux', 'Docker'],
    chapters: [
      {
        title: 'Introduction to Bare Metal Servers',
        slug: 'chapter1',
        readingTime: 10
      },
      {
        title: 'Linux Administration Fundamentals',
        slug: 'chapter2',
        readingTime: 12
      },
      {
        title: 'Domain Names, DNS, and Web Server Basics',
        slug: 'chapter3',
        readingTime: 8
      },
      {
        title: 'Docker and Containerization',
        slug: 'chapter4',
        readingTime: 10
      },
      {
        title: 'Setting Up Core Infrastructure',
        slug: 'chapter5',
        readingTime: 15
      },
      {
        title: 'Advanced Security and Update Management',
        slug: 'chapter6',
        readingTime: 12
      },
      {
        title: 'Web Applications and Development Environment',
        slug: 'chapter7',
        readingTime: 14
      },
      {
        title: 'Comprehensive Quiz',
        slug: 'chapter-comprehensive-quiz',
        readingTime: 20
      },
      {
        title: 'Troubleshooting and Disaster Recovery',
        slug: 'chapter9',
        readingTime: 10
      }
    ]
  },
  {
    id: 'docker-essentials',
    title: 'Docker Essentials for Developers',
    description: 'Master Docker from fundamentals to advanced orchestration techniques for modern application development.',
    difficulty: 'beginner',
    author: 'Mate4Tech',
    lastUpdated: '2025-04-10',
    featuredImage: 'https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7',
    tags: ['Docker', 'Containers', 'DevOps', 'Development'],
    chapters: [
      {
        title: 'Docker Fundamentals',
        slug: 'chapter1',
        readingTime: 8
      },
      {
        title: 'Working with Docker Images',
        slug: 'chapter2',
        readingTime: 10
      },
      {
        title: 'Container Orchestration with Docker Compose',
        slug: 'chapter3',
        readingTime: 12
      },
      {
        title: 'Docker Networking',
        slug: 'chapter4',
        readingTime: 10
      },
      {
        title: 'Docker Volumes and Persistent Data',
        slug: 'chapter5',
        readingTime: 8
      },
      {
        title: 'Multi-stage Builds and Optimization',
        slug: 'chapter6',
        readingTime: 12
      }
    ]
  },
  {
    id: 'minecraft-server',
    title: 'Setting Up a Minecraft Server',
    description: 'Learn how to set up, configure, and maintain your own Minecraft server for optimal gaming experience.',
    difficulty: 'beginner',
    author: 'Mate4Tech',
    lastUpdated: '2025-03-20',
    featuredImage: 'https://images.unsplash.com/photo-1501854140801-50d01698950b',
    tags: ['Gaming', 'Minecraft', 'Server', 'Java'],
    chapters: [
      {
        title: 'Introduction to Minecraft Servers',
        slug: 'chapter1',
        readingTime: 6
      },
      {
        title: 'Server Requirements and Preparation',
        slug: 'chapter2',
        readingTime: 8
      },
      {
        title: 'Installing and Configuring Your Server',
        slug: 'chapter3',
        readingTime: 12
      },
      {
        title: 'Plugins and Server Customization',
        slug: 'chapter4',
        readingTime: 10
      },
      {
        title: 'Server Security and Backups',
        slug: 'chapter5',
        readingTime: 8
      },
      {
        title: 'Troubleshooting Common Issues',
        slug: 'chapter6',
        readingTime: 10
      }
    ]
  },
  {
    id: 'nextjs-15-fullstack',
    title: 'Next.js 15: Complete Fullstack Development',
    description: 'Comprehensive guide to building modern fullstack applications with Next.js 15, including server components and database integration.',
    difficulty: 'intermediate',
    author: 'Mate4Tech',
    lastUpdated: '2025-05-01',
    featuredImage: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1',
    tags: ['React', 'Next.js', 'Fullstack', 'JavaScript', 'Typescript'],
    chapters: [
      {
        title: 'Introduction to Next.js 15',
        slug: 'chapter1',
        readingTime: 8
      },
      {
        title: 'Project Setup and Structure',
        slug: 'chapter2',
        readingTime: 10
      },
      {
        title: 'Client and Server Components',
        slug: 'chapter3',
        readingTime: 15
      },
      {
        title: 'Data Fetching Strategies',
        slug: 'chapter4',
        readingTime: 12
      },
      {
        title: 'Database Integration',
        slug: 'chapter5',
        readingTime: 15
      },
      {
        title: 'Authentication Implementation',
        slug: 'chapter6',
        readingTime: 12
      },
      {
        title: 'Creating API Routes',
        slug: 'chapter7',
        readingTime: 10
      },
      {
        title: 'Deployment and Performance Optimization',
        slug: 'chapter8',
        readingTime: 12
      }
    ]
  },
  {
    id: 'open-source-server-tools',
    title: 'Top 10 Open Source Server Tools',
    description: 'Explore the best open-source tools to enhance your server functionality, from storage solutions to AI frameworks.',
    difficulty: 'intermediate',
    author: 'Mate4Tech',
    lastUpdated: '2025-04-25',
    featuredImage: 'https://images.unsplash.com/photo-1518770660439-4636190af475',
    tags: ['Open Source', 'Server', 'Self-hosted', 'DevOps'],
    chapters: [
      {
        title: 'Introduction to Self-Hosted Open Source Tools',
        slug: 'chapter1',
        readingTime: 8
      },
      {
        title: 'MinIO: Open Source Object Storage',
        slug: 'chapter2',
        readingTime: 12
      },
      {
        title: 'Portainer: Container Management',
        slug: 'chapter3',
        readingTime: 10
      },
      {
        title: 'Grafana: Monitoring and Analytics',
        slug: 'chapter4',
        readingTime: 12
      },
      {
        title: 'Deepseek: AI Language Models',
        slug: 'chapter5',
        readingTime: 15
      },
      {
        title: 'Gitea: Self-hosted Git Service',
        slug: 'chapter6',
        readingTime: 10
      },
      {
        title: 'NextCloud: File Storage and Collaboration',
        slug: 'chapter7',
        readingTime: 12
      },
      {
        title: 'Home Assistant: IoT Integration',
        slug: 'chapter8',
        readingTime: 10
      },
      {
        title: 'Traefik: Edge Router and Proxy',
        slug: 'chapter9',
        readingTime: 8
      },
      {
        title: 'Self-hosted LLM Frameworks',
        slug: 'chapter10',
        readingTime: 12
      }
    ]
  }
];

export const categories = [
  { id: 'devops', name: 'DevOps', description: 'Tools and practices for DevOps', tutorialIds: ['server-admin', 'docker-essentials'], icon: 'server' },
  { id: 'development', name: 'Development', description: 'Software development best practices', tutorialIds: ['nextjs-15-fullstack'], icon: 'code' },
  { id: 'ai', name: 'Artificial Intelligence', description: 'Machine learning and AI concepts', tutorialIds: ['open-source-server-tools'], icon: 'brain' },
  { id: 'security', name: 'Security', description: 'Cybersecurity and data protection', tutorialIds: ['server-admin'], icon: 'shield' },
  { id: 'gaming', name: 'Gaming', description: 'Gaming server setup and management', tutorialIds: ['minecraft-server'], icon: 'server' },
  { id: 'open-source', name: 'Open Source', description: 'Open source tools and frameworks', tutorialIds: ['open-source-server-tools'], icon: 'code' }
];

// Added helper functions to maintain compatibility with existing pages
export const getAllTutorials = (): TutorialMetadata[] => {
  return tutorials;
};

export const getAllCategories = () => {
  return categories;
};

export const getCategoryById = (categoryId: string) => {
  return categories.find(c => c.id === categoryId);
};

export const getTutorialsByCategory = (categoryId: string): TutorialMetadata[] => {
  const category = categories.find(c => c.id === categoryId);
  if (!category) return [];
  return tutorials.filter(tutorial => category.tutorialIds.includes(tutorial.id));
};

export const getTutorialById = (id: string): TutorialMetadata | undefined => {
  return tutorials.find(tutorial => tutorial.id === id);
};

export const getSampleChapter = () => {
  return `# This content will be dynamically loaded from markdown files`;
};
